import { makeAutoObservable } from "mobx";
import { UserOfficeAffiliation } from "le-coffre-resources/dist/Notary";
import UserOfficesApi from "@Front/Api/LeCoffreApi/Notary/UserOffices/UserOffices";
import { LoggerService } from "@Front/Services/LoggerService/LoggerService";
import { logSetActiveOfficeFailure } from "@Front/Stores/activeOfficeStoreSetActiveOfficeCatch";
import {
	loadUserOfficesFromDatabase,
	syncUserOfficesFromIdNot,
	type ActiveOfficeStoreLoadSyncTarget,
} from "@Front/Stores/activeOfficeStoreLoadAndSync";
import { persistRealOfficeBeforeGuestSwitch } from "@Front/Stores/activeOfficeStoreGuestOfficePersistence";
import { invokeUserOfficesSetActive } from "@Front/Stores/activeOfficeStoreSetActiveOfficeApi";
import { finalizeOfficeSwitchAfterApi } from "@Front/Stores/activeOfficeStoreSetActiveOfficeSuccess";
import { clearNotaryInvitedFoldersNavActive } from "@Front/Utils/notaryInvitedFoldersNavPersistence";
import { resolveMenuOfficeUidForGuestApiCall } from "@Front/Stores/activeOfficeStoreGuestMenuOfficeUidForApi";
import { shouldSkipContextSyncWhenGuestListAndHostOfficeIncoming } from "@Front/Stores/activeOfficeStoreGuestContextSyncSkip";
import { ensureNotarySessionReadyForApi } from "@Front/Utils/notarySessionHydrationHelpers";

const LAST_REAL_OFFICE_BEFORE_GUEST_KEY = "lecoffre_last_real_office_before_guest";

class ActiveOfficeStore {
	public activeOffice: UserOfficeAffiliation | null = null;
	public availableOffices: UserOfficeAffiliation[] = [];
	public isLoading = false;
	public error: string | null = null;
	private contextActiveOfficeUid: string | null = null;
	private isChangingOffice = false;
	private lastRealOfficeUidBeforeGuest: string | null = null;
	private loadUserOfficesInFlight: Promise<void> | null = null;
	private loadUserOfficesInFlightUserUid: string | null = null;

	public get officeSwitchInProgress(): boolean {
		return this.isChangingOffice;
	}

	public applyResolvedActiveOffice(offices: UserOfficeAffiliation[]): void {
		this.resolveActiveOffice(offices);
	}

	constructor() {
		makeAutoObservable(this);
	}

	private ensureUserOfficesApiAvailable(): void {
		if (!UserOfficesApi || typeof UserOfficesApi !== "object") {
			const errorMsg = "UserOfficesApi n'est pas disponible. Le module n'a pas pu être chargé correctement.";
			LoggerService.error(`❌ ensureUserOfficesApiAvailable: ${errorMsg}`, {
				UserOfficesApiType: typeof UserOfficesApi,
			});
			throw new Error(errorMsg);
		}

		if (!UserOfficesApi.getInstance || typeof UserOfficesApi.getInstance !== "function") {
			const errorMsg = "UserOfficesApi.getInstance n'est pas disponible. Le module n'a pas pu être initialisé correctement.";
			LoggerService.error(`❌ ensureUserOfficesApiAvailable: ${errorMsg}`, {
				hasGetInstance: "getInstance" in UserOfficesApi,
				getInstanceType: typeof UserOfficesApi.getInstance,
			});
			throw new Error(errorMsg);
		}
	}

	private async loadUserOfficesFromDB(userUid: string): Promise<UserOfficeAffiliation[]> {
		return await loadUserOfficesFromDatabase(this as unknown as ActiveOfficeStoreLoadSyncTarget, userUid);
	}

	public async loadUserOffices(userUid: string): Promise<void> {
		if (!userUid) {
			LoggerService.warn("⚠️ loadUserOffices appelé sans userUid");
			return;
		}

		if (this.loadUserOfficesInFlight && this.loadUserOfficesInFlightUserUid === userUid) {
			return this.loadUserOfficesInFlight;
		}

		const run = async (): Promise<void> => {
			LoggerService.info("🔍 ActiveOfficeStore.loadUserOffices() - chargement", { userUid });
			this.isLoading = true;
			this.error = null;
			try {
				await this.loadUserOfficesFromDB(userUid);
				LoggerService.info("✅ Chargement des offices terminé pour l'utilisateur courant", {
					userUid,
					activeOfficeUid: this.activeOffice?.office_uid,
					activeOfficeName: this.activeOffice?.office?.name,
					count: this.availableOffices.length,
				});
			} catch (error: unknown) {
				LoggerService.error("❌ Impossible de charger les offices depuis la base", { error, userUid });
				this.error = "Impossible de charger les offices";
			} finally {
				this.isLoading = false;
			}
		};

		const promise = run();
		this.loadUserOfficesInFlight = promise;
		this.loadUserOfficesInFlightUserUid = userUid;
		try {
			await promise;
		} finally {
			if (this.loadUserOfficesInFlight === promise) {
				this.loadUserOfficesInFlight = null;
				this.loadUserOfficesInFlightUserUid = null;
			}
		}
	}

	public setActiveOfficeOptions: { redirectTo?: string; currentOfficeUidBeforeGuest?: string } | null = null;

	public async setActiveOffice(
		userUid: string,
		officeUid: string,
		options?: { redirectTo?: string; currentOfficeUidBeforeGuest?: string },
	): Promise<void> {
		this.setActiveOfficeOptions = options ?? null;
		persistRealOfficeBeforeGuestSwitch({
			officeUid,
			options,
			contextActiveOfficeUid: this.contextActiveOfficeUid,
			getActiveOfficeUid: () => this.getActiveOfficeUid(),
			setLastRealOfficeUidBeforeGuest: (uid) => this.setLastRealOfficeUidBeforeGuest(uid),
		});

		LoggerService.info("🔍 Changement d'office demandé pour l'utilisateur courant", {
			userUid,
			officeUidCible: officeUid,
			officeActuel: this.activeOffice?.office?.name ?? this.activeOffice?.office_uid,
		});

		this.isChangingOffice = true;
		this.isLoading = true;
		this.error = null;

		try {
			await ensureNotarySessionReadyForApi();
			const menuOfficeUidForGuestJwt = resolveMenuOfficeUidForGuestApiCall(officeUid, options, () =>
				this.peekLastRealOfficeUidBeforeGuest(),
			);
			const result = await invokeUserOfficesSetActive(
				userUid,
				officeUid,
				() => this.ensureUserOfficesApiAvailable(),
				menuOfficeUidForGuestJwt,
			);
			await finalizeOfficeSwitchAfterApi(this, userUid, officeUid, result);
		} catch (error: unknown) {
			this.error = logSetActiveOfficeFailure(error, userUid, officeUid);
			this.isChangingOffice = false;
			this.isLoading = false;
			throw error;
		}
	}

	public getActiveOffice(): UserOfficeAffiliation | null {
		return this.activeOffice;
	}

	public getActiveOfficeUid(): string | null {
		return this.activeOffice?.office_uid ?? this.activeOffice?.uid ?? this.contextActiveOfficeUid;
	}

	private setLastRealOfficeUidBeforeGuest(uid: string): void {
		this.lastRealOfficeUidBeforeGuest = uid;
		if (typeof window !== "undefined" && window.sessionStorage) {
			window.sessionStorage.setItem(LAST_REAL_OFFICE_BEFORE_GUEST_KEY, uid);
			LoggerService.info("💾 [setLastRealOfficeUidBeforeGuest] Saved to sessionStorage", { uid });
		}
	}

	/**
	 * Dernier office réel avant bascule « dossiers invités », sans log (menus, résolution UI à chaud).
	 */
	public peekLastRealOfficeUidBeforeGuest(): string | null {
		if (this.lastRealOfficeUidBeforeGuest !== null) {
			return this.lastRealOfficeUidBeforeGuest;
		}
		if (typeof window !== "undefined" && window.sessionStorage) {
			const stored = window.sessionStorage.getItem(LAST_REAL_OFFICE_BEFORE_GUEST_KEY);
			if (stored) {
				this.lastRealOfficeUidBeforeGuest = stored;
			}
			return stored;
		}
		return null;
	}

	public getLastRealOfficeUidBeforeGuest(): string | null {
		return this.peekLastRealOfficeUidBeforeGuest();
	}

	public hasMultipleOffices(): boolean {
		return this.availableOffices.length > 1;
	}

	public async syncOfficesFromIdNot(userUid: string): Promise<void> {
		await syncUserOfficesFromIdNot(this as unknown as ActiveOfficeStoreLoadSyncTarget, userUid);
	}

	public reset(): void {
		this.activeOffice = null;
		this.availableOffices = [];
		this.isLoading = false;
		this.error = null;
		this.contextActiveOfficeUid = null;
		this.isChangingOffice = false;
		this.lastRealOfficeUidBeforeGuest = null;
		clearNotaryInvitedFoldersNavActive();
		if (typeof window !== "undefined" && window.sessionStorage) {
			window.sessionStorage.removeItem(LAST_REAL_OFFICE_BEFORE_GUEST_KEY);
		}
	}

	public syncActiveOfficeFromContext(activeOfficeUid: string | null): void {
		const incoming = activeOfficeUid ?? null;
		if (shouldSkipContextSyncWhenGuestListAndHostOfficeIncoming({ activeOffice: this.activeOffice, incoming })) {
			// Le contexte API / JWT peut porter l’office hôte du dossier invité : ne pas remplacer la vue liste
			// « dossiers invités » (sentinel) ni `contextActiveOfficeUid`, pour garder la résolution menu / sélecteur
			// sur l’office métier choisi (`lastRealOfficeUidBeforeGuest` + affiliations réelles).
			return;
		}

		this.contextActiveOfficeUid = incoming;
		if (!incoming) {
			return;
		}

		if (this.activeOffice?.office_uid === incoming || this.activeOffice?.uid === incoming) {
			return;
		}

		const matchingAffiliation = this.availableOffices.find(
			(affiliation) => affiliation.office_uid === incoming || affiliation.uid === incoming,
		);
		if (matchingAffiliation) {
			this.activeOffice = matchingAffiliation;
		}
	}

	private resolveActiveOffice(offices: UserOfficeAffiliation[]): void {
		if (this.contextActiveOfficeUid) {
			const contextualOffice = offices.find(
				(o) => o.office_uid === this.contextActiveOfficeUid || o.uid === this.contextActiveOfficeUid,
			);
			if (contextualOffice) {
				this.activeOffice = contextualOffice;
				return;
			}
		}

		const primary = offices.find((o) => o.is_primary);
		if (primary) {
			this.activeOffice = primary;
		} else if (offices.length > 0) {
			this.activeOffice = offices[0] || null;
		} else {
			this.activeOffice = null;
		}
	}
}

export { ActiveOfficeStore };
